// Demo Auth Provider — calls POST /api/auth/login, stores JWT, provides auth context
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Auth0Context } from '@auth0/auth0-react';
import api from '../services/api';

const DemoAuthContext = createContext(null);

export const useDemoAuth = () => useContext(DemoAuthContext);

// Override useAuth0 to work with demo provider
export const useAuth0 = () => useContext(Auth0Context);

export const DemoAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restore session from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('demo_user');
    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem('access_token');
        localStorage.removeItem('demo_user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.post('/api/auth/login', { email, password });
      const { token: jwt, user: userData } = res.data.data;
      localStorage.setItem('access_token', jwt);
      localStorage.setItem('demo_user', JSON.stringify(userData));
      setToken(jwt);
      setUser(userData);
      return userData;
    } catch (err) {
      const msg = err.response?.data?.error || 'Login failed';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('demo_user');
    setUser(null);
    setToken(null);
    window.location.href = '/login';
  }, []);

  const getAccessTokenSilently = useCallback(async () => {
    return localStorage.getItem('access_token') || '';
  }, []);

  // Provide Auth0-compatible context so existing components work
  const auth0ContextValue = {
    isAuthenticated: !!user,
    user: user ? {
      sub: user.id,
      email: user.email,
      name: user.name,
      picture: user.avatar,
      user_id: user.id,
      'https://angels67.com/roles': user.roles || ['INVESTOR'],
    } : undefined,
    isLoading,
    error: error ? new Error(error) : undefined,
    loginWithRedirect: async () => { window.location.href = '/login'; },
    loginWithPopup: async () => { window.location.href = '/login'; },
    logout: () => logout(),
    getAccessTokenSilently,
    getAccessTokenWithPopup: getAccessTokenSilently,
    getIdTokenClaims: async () => ({
      __raw: token || '',
      name: user?.name,
      email: user?.email,
      picture: user?.avatar,
      sub: user?.id,
    }),
    handleRedirectCallback: async () => ({ appState: {} }),
    buildAuthorizeUrl: async () => '',
    buildLogoutUrl: () => '',
  };

  // Also expose demo-specific context
  const demoContextValue = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
  };

  return (
    <DemoAuthContext.Provider value={demoContextValue}>
      <Auth0Context.Provider value={auth0ContextValue}>
        {children}
      </Auth0Context.Provider>
    </DemoAuthContext.Provider>
  );
};

export default { useAuth0, useDemoAuth, DemoAuthProvider };
